import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import {
  DEFAULT_RADIUS_WIDTH,
  selectedIpdbModelSourceAtom,
  xdaProjectionDimensionsAtom
} from "atoms";
import { useAtom, useAtomValue } from "jotai";
import { RootState } from "store/rootReducer";

import { useRadiusConverter } from "hooks";

import { fetchDimensionsFromPlay } from "api/ipdp";

import useFilteredGeoCalculationsJobs from "components/org-settings/hooks/useFilteredGeoCalculationsJobs";
import { useGetGeoCalculationsJobs } from "components/org-settings/hooks/useGetGeoCalculationsJobs";
import { GeoCalculationsJob } from "components/org-settings/models/GeoCalculations";
import { StickCalculationsJob } from "components/org-settings/models/StickCalculations";

import { THREE_D_MODEL, TWO_D_MODEL } from "../Ipdb";

const SELECTED_RADIUS_STORAGE_KEY = "xda-projection-selected-radius";
const CUSTOM_DIMENSIONS_STORAGE_KEY = "xda-projection-custom-dimensions";

export enum XDA_PROJECTION_RADIUS_TYPES {
  Gross = "Gross",
  SweetSpot = "Sweet Spot",
  LandingZone = "Landing Zone",
  Custom = "Custom"
}

const IPDB_RADIUS_TYPES = [
  XDA_PROJECTION_RADIUS_TYPES.Gross,
  XDA_PROJECTION_RADIUS_TYPES.SweetSpot,
  XDA_PROJECTION_RADIUS_TYPES.LandingZone
];

export enum XDA_DIMENSION_KEYS {
  LeftWidth = "leftWidth",
  RightWidth = "rightWidth"
}

export enum XDA_RADIUS_KEYS {
  Left = "left",
  Right = "right"
}

export interface RadiusDimensions {
  [XDA_RADIUS_KEYS.Left]: number;
  [XDA_RADIUS_KEYS.Right]: number;
}

// Helper to get stored radius from localStorage
const getStoredRadius = (): string => {
  try {
    const stored = localStorage.getItem(SELECTED_RADIUS_STORAGE_KEY);
    return stored || XDA_PROJECTION_RADIUS_TYPES.Gross;
  } catch {
    return XDA_PROJECTION_RADIUS_TYPES.Gross;
  }
};

// Helper to get stored custom dimensions from localStorage
const getStoredCustomDimensions = (): RadiusDimensions => {
  try {
    const stored = localStorage.getItem(CUSTOM_DIMENSIONS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        [XDA_RADIUS_KEYS.Left]:
          typeof parsed[XDA_RADIUS_KEYS.Left] === "number"
            ? parsed[XDA_RADIUS_KEYS.Left]
            : DEFAULT_RADIUS_WIDTH,
        [XDA_RADIUS_KEYS.Right]:
          typeof parsed[XDA_RADIUS_KEYS.Right] === "number"
            ? parsed[XDA_RADIUS_KEYS.Right]
            : DEFAULT_RADIUS_WIDTH
      };
    }
  } catch {
    // Fall through to default
  }
  return {
    [XDA_RADIUS_KEYS.Left]: DEFAULT_RADIUS_WIDTH,
    [XDA_RADIUS_KEYS.Right]: DEFAULT_RADIUS_WIDTH
  };
};

const getDimensionsFromJob = (
  job: GeoCalculationsJob | StickCalculationsJob
): RadiusDimensions => {
  if (XDA_DIMENSION_KEYS.LeftWidth in job && XDA_DIMENSION_KEYS.RightWidth in job) {
    return {
      [XDA_RADIUS_KEYS.Left]: job.leftWidth || 0,
      [XDA_RADIUS_KEYS.Right]: job.rightWidth || 0
    };
  }

  const dimensions =
    (job as StickCalculationsJob).xdaDimensions ||
    (job as StickCalculationsJob).threeDvDimensions;

  if (dimensions) {
    return {
      [XDA_RADIUS_KEYS.Left]: dimensions.leftWidth || 0,
      [XDA_RADIUS_KEYS.Right]: dimensions.rightWidth || 0
    };
  }

  return { [XDA_RADIUS_KEYS.Left]: 0, [XDA_RADIUS_KEYS.Right]: 0 };
};

export function useXdaProjectionOptions() {
  const [xdaDimensions, setXdaDimensions] = useAtom(xdaProjectionDimensionsAtom);
  const updatePlayLegend = useSelector(
    (state: RootState) => state.filter.updatePlayLegend
  );
  const selectedIpdbModelSource = useAtomValue(selectedIpdbModelSourceAtom);

  const { displayLabel, metersToDisplay, displayToMeters, isReady } =
    useRadiusConverter();

  const { data: jobs } = useGetGeoCalculationsJobs();
  const filteredJobs = useFilteredGeoCalculationsJobs(jobs);

  // Initialize from localStorage for persistence across component remounts
  const [selectedRadius, setSelectedRadius] = useState<string>(getStoredRadius);

  const [customDimensions, setCustomDimensions] = useState<RadiusDimensions>(
    getStoredCustomDimensions
  );

  // Persist selectedRadius to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_RADIUS_STORAGE_KEY, selectedRadius);
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [selectedRadius]);

  // Persist customDimensions to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        CUSTOM_DIMENSIONS_STORAGE_KEY,
        JSON.stringify(customDimensions)
      );
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [customDimensions]);

  // Determine play name: Priority is legend filter > IPDB model source
  const playName = useMemo(() => {
    if (updatePlayLegend) return updatePlayLegend;

    if (
      selectedIpdbModelSource &&
      selectedIpdbModelSource !== TWO_D_MODEL &&
      selectedIpdbModelSource !== THREE_D_MODEL
    ) {
      return selectedIpdbModelSource;
    }

    return null;
  }, [updatePlayLegend, selectedIpdbModelSource]);

  // Fetch IPDB dimensions
  const { data: ipdbDimensions } = useQuery(
    ["xdaProjectionDimensions", playName],
    () => fetchDimensionsFromPlay(playName as string),
    {
      enabled: !!playName,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  );

  // Job-based radius options
  const jobRadiusOptions = useMemo(() => {
    if (!filteredJobs?.length) return {};

    return filteredJobs.reduce((acc, job) => {
      acc[job.name] = getDimensionsFromJob(job);
      return acc;
    }, {} as Record<string, RadiusDimensions>);
  }, [filteredJobs]);

  const isCustomRadius = selectedRadius === XDA_PROJECTION_RADIUS_TYPES.Custom;

  // Apply dimensions to atom
  const applyDimensions = useCallback(
    (dimensions: RadiusDimensions) => {
      setXdaDimensions({
        [XDA_DIMENSION_KEYS.LeftWidth]: dimensions[XDA_RADIUS_KEYS.Left],
        [XDA_DIMENSION_KEYS.RightWidth]: dimensions[XDA_RADIUS_KEYS.Right]
      });
    },
    [setXdaDimensions]
  );

  // Initialize dimensions from localStorage on mount
  // This ensures custom dimensions are restored when returning to the component
  useEffect(() => {
    if (selectedRadius === XDA_PROJECTION_RADIUS_TYPES.Custom) {
      applyDimensions(customDimensions);
    } else if (jobRadiusOptions[selectedRadius]) {
      applyDimensions(jobRadiusOptions[selectedRadius]);
    }
    // Intentionally only run on mount - IPDB types are handled by the fetch effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply IPDB dimensions when data is fetched and an IPDB radius type is selected
  useEffect(() => {
    if (!ipdbDimensions?.dimensions) return;

    if (IPDB_RADIUS_TYPES.includes(selectedRadius as XDA_PROJECTION_RADIUS_TYPES)) {
      const targetDimensions = ipdbDimensions.dimensions.find(
        (d: { name: string }) => d.name === selectedRadius
      );

      if (targetDimensions) {
        applyDimensions({
          [XDA_RADIUS_KEYS.Left]: targetDimensions.leftWidth ?? DEFAULT_RADIUS_WIDTH,
          [XDA_RADIUS_KEYS.Right]: targetDimensions.rightWidth ?? DEFAULT_RADIUS_WIDTH
        });
      }
    }
  }, [ipdbDimensions, selectedRadius, applyDimensions]);

  // Handle radius selection change
  const handleRadiusChange = useCallback(
    (value: string) => {
      // Save current dimensions as custom if leaving Custom
      if (selectedRadius === XDA_PROJECTION_RADIUS_TYPES.Custom) {
        setCustomDimensions({
          [XDA_RADIUS_KEYS.Left]: xdaDimensions.leftWidth,
          [XDA_RADIUS_KEYS.Right]: xdaDimensions.rightWidth
        });
      }

      setSelectedRadius(value);

      // Custom - restore saved custom dimensions
      if (value === XDA_PROJECTION_RADIUS_TYPES.Custom) {
        applyDimensions(customDimensions);
        return;
      }

      // Job-based radius options
      if (jobRadiusOptions[value]) {
        applyDimensions(jobRadiusOptions[value]);
        return;
      }

      // IPDB radius types - apply cached dimensions immediately
      if (IPDB_RADIUS_TYPES.includes(value as XDA_PROJECTION_RADIUS_TYPES)) {
        if (ipdbDimensions?.dimensions) {
          const targetDimensions = ipdbDimensions.dimensions.find(
            (d: { name: string }) => d.name === value
          );

          if (targetDimensions) {
            applyDimensions({
              [XDA_RADIUS_KEYS.Left]: targetDimensions.leftWidth ?? DEFAULT_RADIUS_WIDTH,
              [XDA_RADIUS_KEYS.Right]: targetDimensions.rightWidth ?? DEFAULT_RADIUS_WIDTH
            });
          } else {
            // Dimension type not found in API response - apply default
            applyDimensions({
              [XDA_RADIUS_KEYS.Left]: DEFAULT_RADIUS_WIDTH,
              [XDA_RADIUS_KEYS.Right]: DEFAULT_RADIUS_WIDTH
            });
          }
        } else {
          // No IPDB data available - apply default
          applyDimensions({
            [XDA_RADIUS_KEYS.Left]: DEFAULT_RADIUS_WIDTH,
            [XDA_RADIUS_KEYS.Right]: DEFAULT_RADIUS_WIDTH
          });
        }
      }
    },
    [
      jobRadiusOptions,
      applyDimensions,
      ipdbDimensions,
      selectedRadius,
      xdaDimensions,
      customDimensions
    ]
  );

  // Update a single dimension (for custom input)
  const updateDimension = useCallback(
    (dimension: "leftWidth" | "rightWidth", metersValue: number) => {
      setXdaDimensions((prev) => ({
        ...prev,
        [dimension]: metersValue
      }));

      if (selectedRadius === XDA_PROJECTION_RADIUS_TYPES.Custom) {
        setCustomDimensions((prev) => ({
          ...prev,
          [dimension === XDA_DIMENSION_KEYS.LeftWidth
            ? XDA_RADIUS_KEYS.Left
            : XDA_RADIUS_KEYS.Right]: metersValue
        }));
      }
    },
    [setXdaDimensions, selectedRadius]
  );

  return {
    // State
    selectedRadius,
    isCustomRadius,
    jobRadiusOptions,
    xdaDimensions,

    // Unit conversion
    displayLabel,
    metersToDisplay,
    displayToMeters,
    isReady,

    // Actions
    handleRadiusChange,
    updateDimension
  };
}
